import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CssBaseline,
  Divider,
  Link,
  Tooltip,
  Typography,
  Container,
  Paper,
  List,
  ListItem,
  Button,
  ButtonGroup,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import { setBettyLanguage } from "../i18n";
import { getHelpTarget } from "../help/helpTargets";
import {
  getHelpTerm,
  getHelpTerms,
} from "../help/helpTerms";
import { openFeedback } from "../help/feedback";
import {
  whatsNewEntries,
  getWhatsNewText,
} from "../help/whatsNew";

import whatIsBettyDe from "../help/de/what-is-betty.md";
import whatIsBettyEn from "../help/en/what-is-betty.md";

import howDoesBettyDoItDe from "../help/de/how-does-betty-do-it.md";
import howDoesBettyDoItEn from "../help/en/how-does-betty-do-it.md";

import yourFirstStepsDe from "../help/de/your-first-steps.md";
import yourFirstStepsEn from "../help/en/your-first-steps.md";

import understandNumbersDe from "../help/de/understand-the-numbers.md";
import understandNumbersEn from "../help/en/understand-the-numbers.md";

import findValueDe from "../help/de/find-value.md";
import findValueEn from "../help/en/find-value.md";

import checkAssessmentDe from "../help/de/check-your-assessment.md";
import checkAssessmentEn from "../help/en/check-your-assessment.md";

import adjustTeamSettingsDe from "../help/de/adjust-your-team-settings.md";
import adjustTeamSettingsEn from "../help/en/adjust-your-team-settings.md";

import selectLeagueDe from "../help/de/select-a-league.md";
import selectLeagueEn from "../help/en/select-a-league.md";

import prepareTeamSettingsDe from "../help/de/prepare-team-settings.md";
import prepareTeamSettingsEn from "../help/en/prepare-team-settings.md";

import selectFixturesDe from "../help/de/select-fixtures.md";
import selectFixturesEn from "../help/en/select-fixtures.md";

import compileFirstMatchesDe from "../help/de/compile-your-first-matches.md";
import compileFirstMatchesEn from "../help/en/compile-your-first-matches.md";

import probabilitiesFairOddsDe from "../help/de/probabilities-and-fair-odds.md";
import probabilitiesFairOddsEn from "../help/en/probabilities-and-fair-odds.md";

import evaluateFinishedMatchesDe from "../help/de/evaluate-finished-matches.md";
import evaluateFinishedMatchesEn from "../help/en/evaluate-finished-matches.md";

import fixturesDe from "../help/de/fixtures.md";
import fixturesEn from "../help/en/fixtures.md";

import teamSettingsDe from "../help/de/team-settings.md";
import teamSettingsEn from "../help/en/team-settings.md";

import seasonParametersDe from "../help/de/season-parameters.md";
import seasonParametersEn from "../help/en/season-parameters.md";

import compileDe from "../help/de/compile.md";
import compileEn from "../help/en/compile.md";

import evaluateDe from "../help/de/evaluate.md";
import evaluateEn from "../help/en/evaluate.md";

import marketOddsValueDe from "../help/de/market-odds-and-value.md";
import marketOddsValueEn from "../help/en/market-odds-and-value.md";

import simulationDe from "../help/de/simulation.md";
import simulationEn from "../help/en/simulation.md";

import leagueTablesDe from "../help/de/league-tables.md";
import leagueTablesEn from "../help/en/league-tables.md";

import tableStatsDe from "../help/de/table-stats.md";
import tableStatsEn from "../help/en/table-stats.md";

import actualTableDe from "../help/de/actual-table.md";
import actualTableEn from "../help/en/actual-table.md";

import expectedTableDe from "../help/de/expected-table.md";
import expectedTableEn from "../help/en/expected-table.md";

import shouldTableDe from "../help/de/should-table.md";
import shouldTableEn from "../help/en/should-table.md";

import expectedVsActualDe from "../help/de/expected-vs-actual.md";
import expectedVsActualEn from "../help/en/expected-vs-actual.md";

import shouldGoalsDe from "../help/de/should-goals.md";
import shouldGoalsEn from "../help/en/should-goals.md";

import probabilitiesDe from "../help/de/probabilities.md";
import probabilitiesEn from "../help/en/probabilities.md";

import fairOddsDe from "../help/de/fair-odds.md";
import fairOddsEn from "../help/en/fair-odds.md";

import valueDe from "../help/de/value.md";
import valueEn from "../help/en/value.md";

import teamStrengthsDe from "../help/de/team-strengths.md";
import teamStrengthsEn from "../help/en/team-strengths.md";

import homeAdvantageDe from "../help/de/home-advantage.md";
import homeAdvantageEn from "../help/en/home-advantage.md";

import expectedGoalsDe from "../help/de/expected-goals.md";
import expectedGoalsEn from "../help/en/expected-goals.md";

import aboutDirkPaulsenDe from "../help/de/about-dirk-paulsen.md";
import aboutDirkPaulsenEn from "../help/en/about-dirk-paulsen.md";

import glossaryDe from "../help/de/glossary.md";
import glossaryEn from "../help/en/glossary.md";

export function extractFlag(extra) {
  let flag = extra?.flag;

  if (typeof flag === "undefined") {
    return "<?xml version=\"1.0\" encoding=\"UTF-8\"?>\n" +
      "<svg width=\"104.97mm\" height=\"63.663mm\" version=\"1.1\" viewBox=\"0 0 104.97 63.663\" xmlns=\"http://www.w3.org/2000/svg\">\n" +
      "<g transform=\"translate(-18.404 -69.01)\">\n" +
      "<rect x=\"19.588\" y=\"70.676\" width=\"102.6\" height=\"60.192\" fill=\"none\" stroke=\"#000\" stroke-linejoin=\"round\" stroke-width=\"2.5581\" style=\"paint-order:stroke markers fill\"/>\n" +
      "</g>\n" +
      "</svg>\n";
  }

  return flag;
}

function getHelpSections(t) {
  return [
    {
      id: "whats-new",
      title: t("help.whatsNew.title"),
      description: t("help.whatsNew.shortDescription"),
    },
    {
      id: "what-is-betty",
      title: t("help.whatIsBetty.title"),
      description: t("help.whatIsBetty.shortDescription"),
    },
    {
      id: "getting-started",
      title: t("help.gettingStarted.title"),
      description: t("help.gettingStarted.shortDescription"),
    },
    {
      id: "using-betty",
      title: t("help.usingBetty.title"),
      description: t("help.usingBetty.shortDescription"),
    },
    {
      id: "statistics",
      title: t("help.statistics.title"),
      description: t("help.statistics.shortDescription"),
    },
    {
      id: "videos-and-articles",
      title: t("help.videosAndArticles.title"),
      description: t("help.videosAndArticles.shortDescription"),
    },
  ];
}

function HelpSectionCard({ section }) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        minHeight: 180,
      }}
    >
      <CardActionArea
        component="a"
        href={`#${section.id}`}
        sx={{
          height: "100%",
          alignItems: "flex-start",
        }}
      >
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {section.title}
          </Typography>

          <Typography variant="body1" color="text.secondary">
            {section.description}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function HelpTopicList({ topics, onOpenArticle }) {
  return (
    <List dense>
      {topics.map((topic) => (
        <ListItem
          key={topic.article}
          disableGutters
        >
          <Button
            variant="text"
            onClick={() => onOpenArticle(topic.article)}
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            {topic.label}
          </Button>
        </ListItem>
      ))}
    </List>
  );
}


function helpHeadingText(children) {
  return Array.isArray(children)
    ? children
        .map((child) =>
          typeof child === "string" || typeof child === "number"
            ? String(child)
            : ""
        )
        .join(" ")
    : String(children ?? "");
}

function slugifyHelpHeading(children) {
  return helpHeadingText(children)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function resolveHelpAnchor(anchor, language) {
  if (!anchor) return null;

  if (typeof anchor === "string") {
    return anchor;
  }

  if (typeof anchor === "object") {
    return (
      anchor[language] ||
      anchor.en ||
      anchor.de ||
      null
    );
  }

  return null;
}

function isHelpWordCharacter(character) {
  if (!character) return false;
  return /[\p{L}\p{N}_]/u.test(character);
}

function findNextHelpTerm(text, terms, startIndex, usedKeys) {
  const lowerText = text.toLocaleLowerCase();
  let best = null;

  for (const item of terms) {
    if (usedKeys.has(item.key)) continue;

    const needle = String(item.term || "").toLocaleLowerCase();
    if (!needle) continue;

    let searchFrom = startIndex;

    while (searchFrom < text.length) {
      const index = lowerText.indexOf(needle, searchFrom);
      if (index === -1) break;

      const before = index > 0 ? text[index - 1] : "";
      const afterIndex = index + needle.length;
      const after = afterIndex < text.length ? text[afterIndex] : "";

      if (!isHelpWordCharacter(before) && !isHelpWordCharacter(after)) {
        if (
          !best ||
          index < best.index ||
          (index === best.index && needle.length > best.length)
        ) {
          best = { ...item, index, length: needle.length };
        }
        break;
      }

      searchFrom = index + 1;
    }
  }

  return best;
}

function renderHelpText({ text, terms, usedKeys, t, onOpenTarget }) {
  if (typeof text !== "string" || !text) return text;

  const parts = [];
  let cursor = 0;

  while (cursor < text.length) {
    const match = findNextHelpTerm(text, terms, cursor, usedKeys);

    if (!match) {
      parts.push(text.slice(cursor));
      break;
    }

    if (match.index > cursor) {
      parts.push(text.slice(cursor, match.index));
    }

    const displayText = text.slice(match.index, match.index + match.length);
    usedKeys.add(match.key);

    parts.push(
      <Tooltip
        key={`HelpTerm-${match.key}-${match.index}`}
        arrow
        placement="top"
        title={
          <Box sx={{ p: 1, maxWidth: 320 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {displayText}
            </Typography>

            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t(match.tooltipKey)}
            </Typography>

            {match.target && (
              <Button
                size="small"
                variant="text"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onOpenTarget(match.target);
                }}
                sx={{
                  mt: 0.5,
                  px: 0,
                  minWidth: 0,
                  textTransform: "none",
                }}
              >
                {t("help.terms.readMore")}
              </Button>
            )}
          </Box>
        }
      >
        <Link
          component="span"
          underline="always"
          sx={{
            cursor: "help",
            textDecorationStyle: "dotted",
            textDecorationThickness: "1px",
          }}
        >
          {displayText}
        </Link>
      </Tooltip>
    );

    cursor = match.index + match.length;
  }

  return parts;
}

function renderHelpChildren({
  children,
  terms,
  t,
  onOpenTarget,
  usedKeys = new Set(),
}) {
  const items =
    Array.isArray(children)
      ? children
      : [children];

  return items.map((child, index) => {
    if (typeof child === "string") {
      return (
        <span key={`HelpText-${index}`}>
          {renderHelpText({
            text: child,
            terms,
            usedKeys,
            t,
            onOpenTarget,
          })}
        </span>
      );
    }

    if (
      child &&
      typeof child === "object" &&
      child.props &&
      typeof child.props.children !== "undefined"
    ) {
      /*
       * Bestehende Links und Code bleiben unangetastet.
       * Dort darf keine automatische Help-Verlinkung
       * hineingeschachtelt werden.
       */
      if (
        child.type === "a" ||
        child.type === "code" ||
        child.type === "pre"
      ) {
        return child;
      }

      return {
        ...child,
        key:
          child.key ??
          `HelpChild-${index}`,
        props: {
          ...child.props,
          children:
            renderHelpChildren({
              children:
                child.props.children,
              terms,
              t,
              onOpenTarget,
              usedKeys,
            }),
        },
      };
    }

    return child;
  });
}


function escapeHelpTermRegex(value) {
  return String(value).replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&"
  );
}

function helpUrlTransform(url) {
  if (
    typeof url === "string" &&
    (
      url.startsWith("helpterm:") ||
      url.startsWith("help:")
    )
  ) {
    return url;
  }

  return defaultUrlTransform(url);
}


function addHelpTermsToMarkdown(
  markdown,
  terms
) {
  if (
    typeof markdown !== "string" ||
    !markdown ||
    !Array.isArray(terms) ||
    !terms.length
  ) {
    return markdown;
  }

  const usedKeys =
    new Set();

  let insideCodeFence =
    false;

  return markdown
    .split("\n")
    .map((line) => {
      const trimmed =
        line.trim();

      if (
        trimmed.startsWith("```")
      ) {
        insideCodeFence =
          !insideCodeFence;

        return line;
      }

      if (insideCodeFence) {
        return line;
      }

      /*
       * Überschriften bleiben bewusst unangetastet.
       */
      if (
        /^#{1,6}\s/.test(trimmed)
      ) {
        return line;
      }

      let result =
        line;

      for (const item of terms) {
        if (
          usedKeys.has(item.key)
        ) {
          continue;
        }

        const escaped =
          escapeHelpTermRegex(
            item.term
          );

        const pattern =
          new RegExp(
            `(^|[^\\p{L}\\p{N}_])(${escaped})(?=$|[^\\p{L}\\p{N}_])`,
            "iu"
          );

        const match =
          result.match(pattern);

        if (!match) {
          continue;
        }

        const fullMatch =
          match[0];

        const prefix =
          match[1] || "";

        const displayText =
          match[2];

        const replacement =
          `${prefix}[${displayText}](helpterm:${item.key})`;

        result =
          result.replace(
            fullMatch,
            replacement
          );

        usedKeys.add(
          item.key
        );
      }

      return result;
    })
    .join("\n");
}


export function Help() {
  const { t, i18n } = useTranslation();

  const helpSections = getHelpSections(t);

  const helpLanguage =
    String(
      i18n.resolvedLanguage ||
      i18n.language ||
      "en"
    )
      .toLowerCase()
      .startsWith("de")
      ? "de"
      : "en";

  const activeHelpTerms =
    getHelpTerms(helpLanguage);

  const articleUrls = {
    "what-is-betty": {
      de: whatIsBettyDe,
      en: whatIsBettyEn,
    },

    "how-does-betty-do-it": {
      de: howDoesBettyDoItDe,
      en: howDoesBettyDoItEn,
    },

    "your-first-steps": {
      de: yourFirstStepsDe,
      en: yourFirstStepsEn,
    },

    "understand-the-numbers": {
      de: understandNumbersDe,
      en: understandNumbersEn,
    },

    "find-value": {
      de: findValueDe,
      en: findValueEn,
    },

    "check-your-assessment": {
      de: checkAssessmentDe,
      en: checkAssessmentEn,
    },

    "adjust-your-team-settings": {
      de: adjustTeamSettingsDe,
      en: adjustTeamSettingsEn,
    },

    "select-a-league": {
      de: selectLeagueDe,
      en: selectLeagueEn,
    },

    "prepare-team-settings": {
      de: prepareTeamSettingsDe,
      en: prepareTeamSettingsEn,
    },

    "select-fixtures": {
      de: selectFixturesDe,
      en: selectFixturesEn,
    },

    "compile-your-first-matches": {
      de: compileFirstMatchesDe,
      en: compileFirstMatchesEn,
    },

    "probabilities-and-fair-odds": {
      de: probabilitiesFairOddsDe,
      en: probabilitiesFairOddsEn,
    },

    "evaluate-finished-matches": {
      de: evaluateFinishedMatchesDe,
      en: evaluateFinishedMatchesEn,
    },

    fixtures: {
      de: fixturesDe,
      en: fixturesEn,
    },

    "team-settings": {
      de: teamSettingsDe,
      en: teamSettingsEn,
    },

    "season-parameters": {
      de: seasonParametersDe,
      en: seasonParametersEn,
    },

    compile: {
      de: compileDe,
      en: compileEn,
    },

    evaluate: {
      de: evaluateDe,
      en: evaluateEn,
    },

    "market-odds-and-value": {
      de: marketOddsValueDe,
      en: marketOddsValueEn,
    },

    simulation: {
      de: simulationDe,
      en: simulationEn,
    },

    "league-tables": {
      de: leagueTablesDe,
      en: leagueTablesEn,
    },

    "table-stats": {
      de: tableStatsDe,
      en: tableStatsEn,
    },

    "actual-table": {
      de: actualTableDe,
      en: actualTableEn,
    },

    "expected-table": {
      de: expectedTableDe,
      en: expectedTableEn,
    },

    "should-table": {
      de: shouldTableDe,
      en: shouldTableEn,
    },

    "expected-vs-actual": {
      de: expectedVsActualDe,
      en: expectedVsActualEn,
    },

    "should-goals": {
      de: shouldGoalsDe,
      en: shouldGoalsEn,
    },

    probabilities: {
      de: probabilitiesDe,
      en: probabilitiesEn,
    },

    "fair-odds": {
      de: fairOddsDe,
      en: fairOddsEn,
    },

    value: {
      de: valueDe,
      en: valueEn,
    },

    "team-strengths": {
      de: teamStrengthsDe,
      en: teamStrengthsEn,
    },

    "home-advantage": {
      de: homeAdvantageDe,
      en: homeAdvantageEn,
    },

    "expected-goals": {
      de: expectedGoalsDe,
      en: expectedGoalsEn,
    },

    "about-dirk-paulsen": {
      de: aboutDirkPaulsenDe,
      en: aboutDirkPaulsenEn,
    },

    glossary: {
      de: glossaryDe,
      en: glossaryEn,
    },
  };

  const gettingStartedTopics = [
    {
      article: "your-first-steps",
      label: t("help.topics.yourFirstSteps"),
    },
    {
      article: "understand-the-numbers",
      label: t("help.topics.understandNumbers"),
    },
    {
      article: "find-value",
      label: t("help.topics.findValue"),
    },
    {
      article: "check-your-assessment",
      label: t("help.topics.checkAssessment"),
    },
    {
      article: "adjust-your-team-settings",
      label: t("help.topics.adjustTeamSettings"),
    },
  ];

  const usingBettyTopics = [
    {
      article: "fixtures",
      label: t("help.topics.fixtures"),
    },
    {
      article: "team-settings",
      label: t("help.topics.teamSettings"),
    },
    {
      article: "season-parameters",
      label: t("help.topics.seasonParameters"),
    },
    {
      article: "compile",
      label: t("help.topics.compile"),
    },
    {
      article: "evaluate",
      label: t("help.topics.evaluate"),
    },
    {
      article: "market-odds-and-value",
      label: t("help.topics.marketOddsValue"),
    },
    {
      article: "simulation",
      label: t("help.topics.simulation"),
    },
  ];

  const statisticsTopics = [
    {
      article: "table-stats",
      label: t("help.topics.tableStats"),
    },
    {
      article: "actual-table",
      label: t("help.topics.actualTable"),
    },
    {
      article: "expected-goals",
      label: t("help.topics.expectedGoals"),
    },
    {
      article: "should-goals",
      label: t("help.topics.shouldGoals"),
    },
    {
      article: "expected-table",
      label: t("help.topics.expectedTable"),
    },
    {
      article: "should-table",
      label: t("help.topics.shouldTable"),
    },
    {
      article: "expected-vs-actual",
      label: t("help.topics.expectedVsActual"),
    },
  ];


  const [openArticle, setOpenArticle] = useState(null);
  const [openAnchor, setOpenAnchor] = useState(null);
  const [articleContent, setArticleContent] = useState("");
  const [helpHistory, setHelpHistory] = useState([]);

  const renderedArticleContent =
    addHelpTermsToMarkdown(
      articleContent,
      activeHelpTerms
    );

  const changeLanguage = (language) => {
    setBettyLanguage(language);
  };

  const openHelpArticle = (
    article,
    anchor = null,
    {
      addToHistory = true,
    } = {}
  ) => {
    if (!article) return;

    if (
      addToHistory &&
      openArticle
    ) {
      setHelpHistory((history) => [
        ...history,
        {
          article: openArticle,
          anchor: openAnchor,
        },
      ]);
    }

    setOpenArticle(article);
    setOpenAnchor(anchor);
  };

  const openHelpTarget = (targetKey) => {
    const target = getHelpTarget(targetKey);

    if (!target) {
      console.warn(
        "Unknown help target:",
        targetKey
      );
      return;
    }

    openHelpArticle(
      target.article,
      target.anchor || null
    );
  };

  const closeHelpArticle = () => {
    setOpenArticle(null);
    setOpenAnchor(null);
    setHelpHistory([]);
    setArticleContent("");
  };

  const goBackInHelp = () => {
    if (helpHistory.length === 0) {
      closeHelpArticle();
      return;
    }

    const previous =
      helpHistory[helpHistory.length - 1];

    setHelpHistory((history) =>
      history.slice(0, -1)
    );

    setOpenArticle(previous.article);
    setOpenAnchor(previous.anchor || null);
  };

  /*
   * Direkte Help Targets aus normalen UI-Bereichen.
   *
   * Beispiel:
   *   /help?target=expectedTable
   *
   * Der Target-Key wird über helpTargets.js auf
   * Artikel und optionalen Anchor aufgelöst.
   */
  useEffect(() => {
    const params =
      new URLSearchParams(
        window.location.search
      );

    const targetKey =
      params.get("target");

    if (!targetKey) {
      return;
    }

    const target =
      getHelpTarget(targetKey);

    if (!target) {
      console.warn(
        "Unknown help target:",
        targetKey
      );
      return;
    }

    setHelpHistory([]);
    setOpenArticle(
      target.article
    );
    setOpenAnchor(
      target.anchor || null
    );

    /*
     * Target nach dem Öffnen aus der URL entfernen.
     * Reload öffnet dadurch nicht immer wieder
     * zwangsläufig denselben Artikel.
     */
    params.delete("target");

    const query =
      params.toString();

    const cleanUrl =
      window.location.pathname +
      (query ? `?${query}` : "");

    window.history.replaceState(
      {},
      "",
      cleanUrl
    );
  }, []);

  useEffect(() => {
    if (!openArticle) {
      setArticleContent("");
      return;
    }

    const language =
      i18n.language === "de"
        ? "de"
        : "en";

    const markdownUrl =
      articleUrls?.[openArticle]?.[language];

    if (!markdownUrl) {
      setArticleContent("");
      return;
    }

    fetch(markdownUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Could not load help article: ${response.status}`
          );
        }

        return response.text();
      })
      .then((content) => {
        setArticleContent(content);

        window.setTimeout(() => {
          const language =
            i18n.language === "de"
              ? "de"
              : "en";

          const resolvedAnchor =
            resolveHelpAnchor(
              openAnchor,
              language
            );

          if (resolvedAnchor) {
            const target =
              document.getElementById(
                resolvedAnchor
              );

            if (target) {
              target.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });

              return;
            }
          }

          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }, 0);
      })
      .catch((error) => {
        console.error(
          "Could not load help article",
          error
        );

        setArticleContent("");
      });
  }, [
    openArticle,
    openAnchor,
    i18n.language,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(
      window.location.search
    );

    const section = params.get("section");

    if (!section) {
      return;
    }

    const scrollToSection = () => {
      const element =
        document.getElementById(section);

      if (!element) {
        return;
      }

      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

    const timer = window.setTimeout(
      scrollToSection,
      150
    );

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  if (openArticle) {
    return (
      <>
        <CssBaseline />

        <Container
          component="main"
          maxWidth="lg"
          sx={{
            minHeight: "100vh",
            py: 2,
          }}
        >
          <Paper
            sx={{
              p: {
                xs: 2,
                md: 4,
              },
              minHeight: "100vh",
            }}
          >
            <Button
              variant="outlined"
              onClick={goBackInHelp}
              sx={{ mb: 4 }}
            >
              {helpHistory.length > 0
                ? t("help.backInHelp")
                : t("help.backToHelp")}
            </Button>

            {articleContent ? (
              <Box
                sx={{
                  maxWidth: 900,
                  mx: "auto",

                  "& h1": {
                    fontSize: {
                      xs: "2rem",
                      md: "3rem",
                    },
                    lineHeight: 1.15,
                    marginTop: 0,
                    marginBottom: "24px",
                  },

                  "& h2": {
                    fontSize: {
                      xs: "1.5rem",
                      md: "2rem",
                    },
                    lineHeight: 1.25,
                    marginTop: "40px",
                    marginBottom: "16px",
                  },

                  "& p": {
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                    marginTop: 0,
                    marginBottom: "16px",
                  },

                  "& ul": {
                    paddingLeft: "28px",
                    marginBottom: "20px",
                  },

                  "& li": {
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                    marginBottom: "6px",
                  },

                  "& strong": {
                    fontWeight: 700,
                  },
                }}
              >
                <ReactMarkdown
                  urlTransform={helpUrlTransform}
                  components={{
                    h1: ({
                      children,
                      ...props
                    }) => (
                      <h1
                        id={slugifyHelpHeading(
                          children
                        )}
                        {...props}
                      >
                        {children}
                      </h1>
                    ),

                    h2: ({
                      children,
                      ...props
                    }) => (
                      <h2
                        id={slugifyHelpHeading(
                          children
                        )}
                        {...props}
                      >
                        {children}
                      </h2>
                    ),

                    h3: ({
                      children,
                      ...props
                    }) => (
                      <h3
                        id={slugifyHelpHeading(
                          children
                        )}
                        {...props}
                      >
                        {children}
                      </h3>
                    ),

                    p: ({ children, ...props }) => (
                      <p {...props}>
                        {children}
                      </p>
                    ),

                    li: ({ children, ...props }) => (
                      <li {...props}>
                        {children}
                      </li>
                    ),

                    a: ({
                      href,
                      children,
                      ...props
                    }) => {
                      if (
                        href?.startsWith("helpterm:")
                      ) {
                        const termKey =
                          href.slice(
                            "helpterm:".length
                          );

                        const term =
                          getHelpTerm(
                            termKey
                          );

                        if (!term) {
                          return children;
                        }

                        return (
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              <Box
                                sx={{
                                  p: 1,
                                  maxWidth: 320,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 800,
                                  }}
                                >
                                  {children}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    mt: 0.5,
                                  }}
                                >
                                  {t(
                                    term.tooltipKey
                                  )}
                                </Typography>

                                {term.target && (
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      openHelpTarget(
                                        term.target
                                      );
                                    }}
                                    sx={{
                                      mt: 0.5,
                                      px: 0,
                                      minWidth: 0,
                                      textTransform:
                                        "none",
                                    }}
                                  >
                                    {t(
                                      "help.terms.readMore"
                                    )}
                                  </Button>
                                )}
                              </Box>
                            }
                          >
                            <Link
                              component="span"
                              underline="always"
                              sx={{
                                cursor: "help",
                                color: "inherit",
                                textDecorationStyle:
                                  "dotted",
                              }}
                            >
                              {children}
                            </Link>
                          </Tooltip>
                        );
                      }

                      if (
                        href?.startsWith("help:")
                      ) {
                        const targetKey =
                          href.slice(
                            "help:".length
                          );

                        return (
                          <Link
                            component="button"
                            type="button"
                            onClick={() =>
                              openHelpTarget(
                                targetKey
                              )
                            }
                            sx={{
                              verticalAlign:
                                "baseline",
                              cursor: "pointer",
                            }}
                          >
                            {children}
                          </Link>
                        );
                      }

                      return (
                        <Link
                          href={href}
                          {...props}
                        >
                          {children}
                        </Link>
                      );
                    },
                  }}
                >
                  {renderedArticleContent}
                </ReactMarkdown>
              </Box>
            ) : (
              <Typography color="text.secondary">
                {t("help.loadingArticle")}
              </Typography>
            )}
          </Paper>
        </Container>
      </>
    );
  }

  return (
    <>
      <CssBaseline />

      <Container
        component="main"
        maxWidth="xl"
        sx={{
          minHeight: "100vh",
          py: 2,
        }}
      >
        <Paper
          sx={{
            p: {
              xs: 2,
              md: 4,
            },
            minHeight: "100vh",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              mb: 1,
            }}
          >
            <Typography
              component="h1"
              variant="h2"
              gutterBottom
            >
              {t("help.title")}
            </Typography>

            <ButtonGroup
              size="small"
              aria-label="Language"
            >
              <Button
                variant={
                  i18n.language === "en"
                    ? "contained"
                    : "outlined"
                }
                onClick={() =>
                  changeLanguage("en")
                }
              >
                EN
              </Button>

              <Button
                variant={
                  i18n.language === "de"
                    ? "contained"
                    : "outlined"
                }
                onClick={() =>
                  changeLanguage("de")
                }
              >
                DE
              </Button>
            </ButtonGroup>
          </Box>

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 900,
              mb: 4,
            }}
          >
            {t("help.intro")}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
              mb: 6,
            }}
          >
            {helpSections.map((section) => (
              <HelpSectionCard
                key={section.id}
                section={section}
              />
            ))}
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box
            component="section"
            id="whats-new"
            sx={{
              scrollMarginTop: 24,
              mb: 6,
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
            >
              {t("help.whatsNew.overline")}
            </Typography>

            <Typography
              component="h2"
              variant="h3"
              gutterBottom
            >
              {t("help.whatsNew.title")}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: 900,
                mb: 3,
              }}
            >
              {t("help.whatsNew.intro")}
            </Typography>

            {whatsNewEntries.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  maxWidth: 900,
                }}
              >
                {whatsNewEntries.map(
                  (entry, index) => (
                    <Card
                      key={entry.id}
                      variant="outlined"
                      sx={{
                        borderWidth:
                          index === 0
                            ? 2
                            : 1,
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent:
                              "space-between",
                            alignItems:
                              "flex-start",
                            gap: 2,
                            mb: 1,
                          }}
                        >
                          <Typography
                            component="h3"
                            variant="h5"
                          >
                            {getWhatsNewText(
                              entry,
                              "title",
                              helpLanguage
                            )}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              whiteSpace:
                                "nowrap",
                            }}
                          >
                            {entry.publishedAt}
                          </Typography>
                        </Box>

                        <Typography
                          variant="body1"
                          sx={{
                            mb: 2,
                          }}
                        >
                          {getWhatsNewText(
                            entry,
                            "shortText",
                            helpLanguage
                          )}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.7,
                            mb:
                              entry.helpTarget
                                ? 2
                                : 0,
                          }}
                        >
                          {getWhatsNewText(
                            entry,
                            "body",
                            helpLanguage
                          )}
                        </Typography>

                        {entry.helpTarget && (
                          <Button
                            variant="text"
                            onClick={() =>
                              openHelpTarget(
                                entry.helpTarget
                              )
                            }
                            sx={{
                              px: 0,
                              textTransform:
                                "none",
                            }}
                          >
                            {t(
                              "help.whatsNew.readMore"
                            )}
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  )
                )}
              </Box>
            ) : (
              <Typography
                color="text.secondary"
              >
                {t("help.whatsNew.empty")}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box
            component="section"
            id="what-is-betty"
            sx={{
              scrollMarginTop: 24,
              mb: 6,
            }}
          >
            <Typography
              component="h2"
              variant="h3"
              gutterBottom
            >
              {t("help.whatIsBetty.title")}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: 900,
                mb: 2,
              }}
            >
              {t("help.whatIsBetty.intro")}
            </Typography>

            <Button
              variant="outlined"
              onClick={() =>
                openHelpArticle(
                  "what-is-betty"
                )
              }
            >
              {t("help.whatIsBetty.readMore")}
            </Button>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box
            component="section"
            id="getting-started"
            sx={{
              scrollMarginTop: 24,
              mb: 6,
            }}
          >
            <Typography
              component="h2"
              variant="h3"
              gutterBottom
            >
              {t("help.gettingStarted.title")}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {t("help.gettingStarted.intro")}
            </Typography>

            <HelpTopicList
              topics={gettingStartedTopics}
              onOpenArticle={openHelpArticle}
            />
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box
            component="section"
            id="using-betty"
            sx={{
              scrollMarginTop: 24,
              mb: 6,
            }}
          >
            <Typography
              component="h2"
              variant="h3"
              gutterBottom
            >
              {t("help.usingBetty.title")}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {t("help.usingBetty.intro")}
            </Typography>

            <HelpTopicList
              topics={usingBettyTopics}
              onOpenArticle={openHelpArticle}
            />
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box
            component="section"
            id="statistics"
            sx={{
              scrollMarginTop: 24,
              mb: 6,
            }}
          >
            <Typography
              component="h2"
              variant="h3"
              gutterBottom
            >
              {t("help.statistics.title")}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {t("help.statistics.intro")}
            </Typography>

            <HelpTopicList
              topics={statisticsTopics}
              onOpenArticle={openHelpArticle}
            />
          </Box>

          <Divider sx={{ my: 4 }} />

          <Divider sx={{ my: 4 }} />

          <Box
            component="section"
            id="videos-and-articles"
            sx={{
              scrollMarginTop: 24,
              mb: 6,
            }}
          >
            <Typography
              component="h2"
              variant="h3"
              gutterBottom
            >
              {t("help.videosAndArticles.title")}
            </Typography>

            <Typography
              component="h3"
              variant="h4"
              sx={{
                mt: 3,
                mb: 2,
              }}
            >
              {t("help.videosAndArticles.videos")}
            </Typography>

            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: 1120,
                aspectRatio: "16 / 9",
                mb: 4,
              }}
            >
              <iframe
                src="https://www.youtube-nocookie.com/embed/keUq2_imtvs"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                }}
              />
            </Box>

            <Typography
              component="h3"
              variant="h4"
              sx={{
                mt: 3,
                mb: 2,
              }}
            >
              {t("help.videosAndArticles.articles")}
            </Typography>

            <List>
              <ListItem>
                <Link
                  href="https://blog.betty.works/posts/1-introduction-to-sports-betting"
                  target="_blank"
                  rel="noreferrer"
                >
                  Introduction to sports betting
                </Link>
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 5 }} />

          <Box
            component="section"
            sx={{
              maxWidth: 900,
              py: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
            >
              {t("help.glossary.overline")}
            </Typography>

            <Typography
              component="h2"
              variant="h5"
              gutterBottom
            >
              {t("help.glossary.title")}
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                mb: 2,
              }}
            >
              {t("help.glossary.description")}
            </Typography>

            <Button
              variant="text"
              onClick={() =>
                openHelpArticle(
                  "glossary"
                )
              }
              sx={{
                px: 0,
              }}
            >
              {t("help.glossary.open")}
            </Button>
          </Box>

          <Divider sx={{ my: 5 }} />

          <Box
            component="section"
            sx={{
              maxWidth: 900,
              py: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
            >
              {t("help.feedback.overline")}
            </Typography>

            <Typography
              component="h2"
              variant="h5"
              gutterBottom
            >
              {t("help.feedback.title")}
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                mb: 2,
              }}
            >
              {t("help.feedback.description")}
            </Typography>

            <Button
              variant="contained"
              onClick={() =>
                openFeedback({
                  type: "feedback",
                  context: "Help",
                  language:
                    i18n.language === "de"
                      ? "de"
                      : "en",
                })
              }
            >
              {t("help.feedback.button")}
            </Button>
          </Box>

          <Divider sx={{ my: 5 }} />

          <Box
            component="section"
            sx={{
              maxWidth: 900,
              py: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
            >
              betty background
            </Typography>

            <Typography
              component="h2"
              variant="h5"
              gutterBottom
            >
              {t("help.aboutDirkPaulsen.title")}
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                mb: 2,
              }}
            >
              {t("help.aboutDirkPaulsen.description")}
            </Typography>

            <Button
              variant="text"
              onClick={() =>
                openHelpArticle(
                  "about-dirk-paulsen"
                )
              }
              sx={{
                px: 0,
              }}
            >
              {t("help.aboutDirkPaulsen.readMore")}
            </Button>
          </Box>
        </Paper>
      </Container>
    </>
  );
}
